/**
 * How a LinkedIn stage reads in the CRM (LightCRM kanban), shared by the LinkedIn page and the
 * kanban so both suggest the same move. The CRM is never moved on its own: the rep clicks.
 */

/** A LinkedIn table other than À relancer (a follow-up is not a stage). */
export type LinkedinStage = "discover" | "invited" | "accepted" | "messaged" | "replied" | "meeting" | "signed";

export const LINKEDIN_STAGE_LABEL: Record<LinkedinStage, string> = {
    discover: "Pas encore invité",
    invited: "Invitation envoyée",
    accepted: "Invitation acceptée",
    messaged: "Message envoyé",
    replied: "A répondu",
    meeting: "RDV pris",
    signed: "Signé",
};

/** The kanban columns, in board order, with the status a move into each one stores. */
export const CRM_COLUMNS = [
    { status: "new", label: "Leads" },
    { status: "contacted", label: "Contactés" },
    { status: "qualified", label: "Negotiation" },
    { status: "responded", label: "En cours" },
    { status: "won", label: "Clôturés" },
] as const;

/** Index in `CRM_COLUMNS` of the column a stored status shows in. */
export function crmColumnIndex(status: string): number {
    if (status === "contacted") return 1;
    if (status === "qualified") return 2;
    if (status === "responded" || status === "proposal") return 3;
    if (status === "won" || status === "lost") return 4;
    return 0;
}

const STAGE_COLUMN: Record<LinkedinStage, number> = {
    discover: 0,
    invited: 1,
    accepted: 1,
    messaged: 1,
    replied: 2,
    meeting: 3,
    signed: 4,
};

/** The column a new CRM card should land in, for a prospect at this LinkedIn stage. */
export const crmStatusFor = (stage: LinkedinStage) => CRM_COLUMNS[STAGE_COLUMN[stage]].status;

/**
 * The move the LinkedIn stage calls for, or null: only forward, and never out of a closed or
 * inactive card — a lost deal stays lost whatever LinkedIn says.
 */
export function crmSuggestion(stage: LinkedinStage, crmStatus: string): { status: string; label: string } | null {
    if (["won", "lost", "inactive"].includes(crmStatus)) return null;
    const target = STAGE_COLUMN[stage];
    if (target <= crmColumnIndex(crmStatus)) return null;
    return CRM_COLUMNS[target];
}
